<template>
  <Teleport to="body">
    <transition name="ai-panel">
      <aside
        v-if="show"
        ref="panelEl"
        :class="[
          isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
          'fixed top-0 right-0 h-screen z-40 flex flex-col border-l shadow-2xl'
        ]"
        :style="{ width: widthPx + 'px' }"
        role="complementary"
        aria-label="Atlas AI panel"
      >
        <AiChatPanelResizer />

        <!-- Header -->
        <div
          :class="[
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200',
            'flex items-center justify-between gap-2 px-3 py-2 border-b shrink-0'
          ]"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="text-base"
              aria-hidden="true"
              >🤖</span
            >
            <div class="min-w-0">
              <p
                :class="[
                  isDark ? 'text-gray-100' : 'text-gray-900',
                  'font-semibold text-sm leading-none truncate'
                ]"
              >
                Atlas AI
              </p>
              <p
                :class="[
                  isDark ? 'text-gray-400' : 'text-gray-500',
                  'text-[11px] leading-tight mt-0.5 truncate'
                ]"
                :title="routePath"
              >
                {{ headerSubtitle }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <RouterLink
              to="/database/atlas-intel/atlas-ai-history"
              :class="[
                isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
                'text-[11px] hover:underline px-1'
              ]"
              title="Open the full Atlas AI Chat page"
              >Open chat ↗</RouterLink
            >
            <button
              :class="[
                isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
                'p-1 rounded'
              ]"
              :title="`Close (${shortcutLabel('Esc')})`"
              aria-label="Close Atlas AI panel"
              @click="panel.close()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Context chips -->
        <div
          v-if="visibleChips.length"
          :class="[
            isDark ? 'border-gray-700' : 'border-gray-200',
            'flex flex-wrap gap-1.5 px-3 py-2 border-b shrink-0'
          ]"
        >
          <ContextChip
            v-for="chip in visibleChips"
            :id="chip.id"
            :key="chip.id"
            :label="chip.label"
            :kind="chip.kind"
            :removable="chip.removable !== false"
            @remove="onRemoveChip"
          />
        </div>

        <!-- Quick-action chips (only when thread is fresh) -->
        <div
          v-if="quickActions.length && !hasSeededQuestion"
          :class="[
            isDark ? 'border-gray-700' : 'border-gray-200',
            'flex flex-col gap-1.5 px-3 py-2 border-b shrink-0'
          ]"
        >
          <p
            :class="[
              isDark ? 'text-gray-400' : 'text-gray-500',
              'text-[10px] uppercase tracking-wide font-medium'
            ]"
          >
            Try asking
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="q in quickActions"
              :key="q"
              type="button"
              :class="[
                isDark
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300',
                'text-left text-[11px] leading-snug px-2 py-1 rounded-md border transition-colors'
              ]"
              @click="onQuickAction(q)"
            >
              {{ q }}
            </button>
          </div>
        </div>

        <!-- Message list (mounts the existing React chat for transport) -->
        <ChatMessageList
          :connection-id="activeChatId"
          :auto-run-question="initialAutoRun"
          class="flex-1 min-h-0"
        />

        <!-- Footer hint -->
        <div
          :class="[
            isDark
              ? 'border-gray-700 bg-gray-900 text-gray-500'
              : 'border-gray-200 bg-gray-50 text-gray-500',
            'border-t px-3 py-1.5 text-[10px] shrink-0 flex justify-between items-center'
          ]"
        >
          <span
            >{{ shortcutLabel('⌘K') }} toggle · {{ shortcutLabel('⌘/') }} focus · Esc close</span
          >
          <button
            v-if="mutedChipCount > 0"
            class="hover:underline"
            @click="panel.unmuteAllChips()"
          >
            Restore {{ mutedChipCount }} chip{{ mutedChipCount === 1 ? '' : 's' }}
          </button>
        </div>
      </aside>
    </transition>
  </Teleport>
</template>

<script lang="ts">
/**
 * AiChatPanel
 *
 * Slide-in AI panel pinned to the right edge of the viewport. Owns:
 *   - global keybindings (Cmd/Ctrl+K toggle, Esc close, Cmd+/ focus, Cmd+Enter send)
 *   - body padding-right so SidePanel main content shrinks while the panel is open
 *   - per-route thread plumbing (route → connectionId via AiChatPanelStore)
 *   - context chip + quick-action UI
 *
 * Transport (message list, streaming, sessions, persistence) is delegated to
 * the existing React query-ai chat via ChatMessageList.vue → mountAIChat().
 */
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  inject,
  nextTick
} from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import { useAiChatPanelStore } from '../../stores/ai-chat-panel.store'
import { useAiChatContext } from '../../composables/useAiChatContext'
import ContextChip from './ContextChip.vue'
import ChatMessageList from './ChatMessageList.vue'
import AiChatPanelResizer from './AiChatPanelResizer.vue'

const QUICK_ACTIONS_BY_PATH: Record<string, string[]> = {
  '/database/atlas-intel/recommendations': ['Why was this rejected by review?', 'Show evidence'],
  // Atlas Intel uses /costs (not /costs/) — match exact route
  '/database/atlas-intel/costs': [
    'Top 3 cost drivers this month',
    'What changed vs last month?',
    'Savings opportunities'
  ],
  '/database/atlas-intel/ops': [
    'Why are reads spiking?',
    'Which collection?',
    'Compare to last week'
  ],
  '/database/atlas-intel/storage': [
    'Largest collections',
    'Index/data ratio outliers',
    'Bloat candidates'
  ],
  '/database/atlas-intel/firestore-estimator': [
    'Why is Firestore more expensive?',
    'Best CUD term',
    'Cluster ranking by savings'
  ]
}

export default defineComponent({
  name: 'AiChatPanel',
  components: { RouterLink, ContextChip, ChatMessageList, AiChatPanelResizer },
  setup() {
    const theme = inject('theme', useTheme())
    const isDark = computed(() => theme.isDark.value)
    const route = useRoute()
    const panel = useAiChatPanelStore()
    // Wire context derivation; this writes through to panel.contextChips on every update.
    const { chips: visibleChipsList } = useAiChatContext()

    const panelEl = ref<HTMLElement | null>(null)

    // ── Per-route thread plumbing ────────────────────────────────────────────
    watch(
      () => route.path,
      (path) => {
        panel.setRoutePath(path)
      },
      { immediate: true }
    )

    const activeChatId = computed(() => panel.activeChatId)
    const widthPx = computed(() => panel.widthPx)
    const visibleChips = computed(() => visibleChipsList.value)
    const mutedChipCount = computed(() => panel.mutedChipIds.size)
    const routePath = computed(() => route.path)

    const headerSubtitle = computed(() => {
      const p = route.path
      if (!p) return 'Per-route thread'
      const seg = p.split('/').filter(Boolean).pop() || 'home'
      return `Thread: ${seg}`
    })

    const quickActions = computed<string[]>(() => QUICK_ACTIONS_BY_PATH[route.path] ?? [])

    // The React chat tracks its own message list. We track whether *this*
    // panel session has dispatched at least one seed question, so quick-actions
    // can hide once the thread isn't fresh anymore.
    const hasSeededQuestion = computed(() => panel.seedNonce > 0)
    const initialAutoRun = ref<string>('')

    // ── Show only inside Atlas Intel route subtree ───────────────────────────
    const inAtlasIntel = computed(() => route.path.startsWith('/database/atlas-intel'))

    // Routes that *are* a full-page chat surface (the page itself renders the
    // chat). On these routes the right-rail panel is suppressed entirely so
    // users don't get a duplicate "small" chat next to the page chat. Match
    // by exact path so deep links inside other Atlas Intel pages aren't
    // accidentally caught.
    const FULL_PAGE_CHAT_ROUTES: ReadonlySet<string> = new Set([
      '/database/clickhouse-ai/chat',
      '/database/atlas-intel/atlas-ai-history'
    ])
    const isFullPageChatRoute = computed(() => FULL_PAGE_CHAT_ROUTES.has(route.path))

    // Auto-close (without erasing state) when navigating into a full-page chat
    // surface. The panel re-appears in its previous open/closed state once the
    // user navigates back to a non-full-page-chat route — we only suppress
    // *display* here, not the persisted open flag, so manual open via Cmd+K
    // outside these routes still works.
    watch(
      isFullPageChatRoute,
      (full) => {
        if (full && panel.open) panel.close()
      },
      { immediate: true }
    )

    const show = computed(() => inAtlasIntel.value && panel.open && !isFullPageChatRoute.value)

    // ── body padding so SidePanel main content shrinks ──────────────────────
    function applyBodyPadding() {
      if (typeof document === 'undefined') return
      if (show.value) {
        document.body.style.paddingRight = `${widthPx.value}px`
        document.body.style.transition = 'padding-right 0.2s ease'
      } else {
        document.body.style.paddingRight = ''
      }
    }
    watch([show, widthPx], applyBodyPadding, { immediate: false })
    onMounted(() => {
      applyBodyPadding()
    })
    onBeforeUnmount(() => {
      if (typeof document !== 'undefined') document.body.style.paddingRight = ''
      if (typeof window !== 'undefined')
        window.removeEventListener('keydown', onGlobalKeyDown, {
          capture: true
        } as EventListenerOptions)
    })

    // ── Global keybindings ──────────────────────────────────────────────────
    function onGlobalKeyDown(e: KeyboardEvent) {
      if (!inAtlasIntel.value) return
      // Don't intercept Cmd/Ctrl+K (or any other panel shortcuts) on full-page
      // chat routes — the page chat owns its own keybindings there.
      if (isFullPageChatRoute.value) return
      const meta = e.metaKey || e.ctrlKey
      const target = e.target as HTMLElement | null
      const inEditable =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)

      // Cmd/Ctrl+K → toggle (always, even from inputs — matches spec)
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        panel.toggle()
        return
      }
      if (!panel.open) return

      // Esc → close (only if focus is in the panel or not in another input)
      if (e.key === 'Escape') {
        if (!inEditable || isInsidePanel(target)) {
          e.preventDefault()
          panel.close()
        }
        return
      }

      // Cmd/Ctrl+/ → focus chat input inside panel
      if (meta && e.key === '/') {
        e.preventDefault()
        focusInput()
        return
      }

      // Cmd/Ctrl+Enter → send the current input value
      if (meta && (e.key === 'Enter' || e.key === 'Return')) {
        if (isInsidePanel(target)) {
          // Let the React side's own keyboard handler handle it.
          return
        }
        // Outside the panel: best-effort focus then synthesize the send by
        // forwarding focus into the input. The React chat already binds
        // Cmd+Enter; once we focus the input the user can press it again.
        focusInput()
      }
    }

    function isInsidePanel(el: Element | null): boolean {
      if (!el || !panelEl.value) return false
      return panelEl.value.contains(el)
    }

    function focusInput() {
      if (!panelEl.value) return
      const sel = 'textarea, [contenteditable="true"], input[type="text"], input[type="search"]'
      const candidates = Array.from(panelEl.value.querySelectorAll<HTMLElement>(sel)).filter(
        (n) => !n.hasAttribute('readonly') && !(n as HTMLInputElement).disabled
      )
      if (candidates.length === 0) return
      // Prefer the LAST input — typically the chat composer at the bottom.
      const target = candidates[candidates.length - 1]
      target.focus()
      if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
        const len = target.value.length
        target.setSelectionRange(len, len)
      }
    }

    onMounted(() => {
      if (typeof window === 'undefined') return
      window.addEventListener('keydown', onGlobalKeyDown, { capture: true })
    })

    // Refocus input when panel opens
    watch(show, async (v) => {
      if (!v) return
      await nextTick()
      // Give the React chat a tick to mount its textarea
      setTimeout(focusInput, 60)
    })

    function shortcutLabel(label: string): string {
      // Replace ⌘ with Ctrl on non-Mac
      if (typeof navigator === 'undefined') return label
      const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '')
      if (isMac) return label
      return label.replace('⌘', 'Ctrl+')
    }

    function onRemoveChip(id: string) {
      panel.clearChip(id)
    }

    function onQuickAction(q: string) {
      panel.send(q, { autoRun: true })
    }

    return {
      isDark,
      panel,
      panelEl,
      show,
      widthPx,
      activeChatId,
      visibleChips,
      mutedChipCount,
      routePath,
      headerSubtitle,
      quickActions,
      hasSeededQuestion,
      initialAutoRun,
      onRemoveChip,
      onQuickAction,
      shortcutLabel
    }
  }
})
</script>

<style scoped>
.ai-panel-enter-active,
.ai-panel-leave-active {
  transition:
    transform 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.18s ease;
}
.ai-panel-enter-from,
.ai-panel-leave-to {
  transform: translateX(100%);
  opacity: 0.6;
}
</style>
